import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { AnimalSheltersRepository } from '../../../shared/states/animal-shelter.repository';
import { HelperService } from '../../../shared/services/helper.service';
import { AnimalCategory } from '../../animals/models/animal-category.enum';
import {
  buildMonthGrid,
  CalendarDay,
  DueTreatmentGroup,
  groupDueTreatments,
  groupDueTreatmentsByDay,
  monthRange
} from '../../animals/models/medical/due-treatment.helpers';
import {
  DueTreatmentStatus,
  getDueTreatmentStatusLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  PreventiveTreatmentCategory
} from '../../animals/models/medical/medical.enums';
import { addMonths, toIsoDateString, todayIsoDate } from '../../animals/models/medical/medical.helpers';
import { AnimalRepository } from '../../animals/states/animal.repository';
import { DueTreatmentRepository } from '../../animals/states/medical/due-treatment.repository';

/** Eine Fälligkeit mit allen fertig übersetzten Beschriftungen für die Anzeige. */
export interface DueTreatmentRow extends DueTreatmentGroup {
  /** Erreger als Liste, bei leeren Erregern die Kategorie. */
  label: string;
  typeLabels: string[];
  categoryLabel: string;
  speciesLabel: string;
  animalLabel: string;
  initial: string;
  avatarColor: string;
  daysLabel: string;
  vetName: string;
  notInStock: boolean;
}

interface StatusTile {
  key: DueTreatmentStatus;
  count: number;
  active: boolean;
}

interface ListSection {
  key: DueTreatmentStatus;
  rows: DueTreatmentRow[];
}

/** Hintergrund der Tier-Avatare je Tierart, angelehnt an die Design-Vorlage. */
const SPECIES_COLORS: Record<string, string> = {
  [AnimalCategory.DOG]: '#e4eed4',
  [AnimalCategory.CAT]: '#eaf1fb',
  [AnimalCategory.HORSE]: '#e8e1d3',
  [AnimalCategory.SMALL_ANIMAL]: '#f3eefb',
};
const DEFAULT_AVATAR_COLOR = '#f3f3f0';

/** Das weiteste Fenster, das die Zeitraum-Auswahl anbietet — so weit wird einmal geladen. */
const MAX_HORIZON_DAYS = 90;

const STATUS_ORDER: DueTreatmentStatus[] = [
  DueTreatmentStatus.OVERDUE,
  DueTreatmentStatus.DUE,
  DueTreatmentStatus.UPCOMING,
];

/**
 * Fälligkeiten über alle Tiere des aktiven Tierheims, als Liste und als Monatskalender.
 * Beides liest `GET /medical/due_treatments`.
 */
@Component({
  selector: 'app-dashboard-due-treatments',
  templateUrl: './dashboard-due-treatments.component.html',
  styleUrls: ['./dashboard-due-treatments.component.scss'],
  standalone: false
})
export class DashboardDueTreatmentsComponent implements OnInit, OnDestroy {
  view: 'list' | 'calendar' = 'list';

  /** Statusfilter der KPI-Kacheln. */
  statusActive: Record<string, boolean> = {
    [DueTreatmentStatus.OVERDUE]: true,
    [DueTreatmentStatus.DUE]: true,
    [DueTreatmentStatus.UPCOMING]: true,
  };
  category: PreventiveTreatmentCategory | null = null;
  species: string | null = null;
  horizonDays = 30;
  onlyInStock = true;

  categoryOptions: { name: string; value: string }[] = [];
  speciesOptions: { name: string; value: string }[] = [];
  horizonOptions: { name: string; value: number }[] = [];

  tiles: StatusTile[] = [];
  sections: ListSection[] = [];
  loadingList = false;

  monthDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  grid: CalendarDay[] = [];
  byDay: Record<string, DueTreatmentRow[]> = {};
  selectedDay: string = todayIsoDate();
  loadingCalendar = false;

  weekdays: string[] = [];
  shelterName = '';

  protected readonly statusOrder = STATUS_ORDER;
  protected readonly statusLabelKey = getDueTreatmentStatusLabelKey;

  /** Alle geladenen Fälligkeiten bis `MAX_HORIZON_DAYS`, ungefiltert. */
  private _allRows: DueTreatmentRow[] = [];
  private _monthRows: DueTreatmentRow[] = [];
  private _outOfStockIds = new Set<number>();
  private _shelterId: number | null = null;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dueTreatmentRepository: DueTreatmentRepository,
    private readonly _sheltersRepository: AnimalSheltersRepository,
    private readonly _animalRepository: AnimalRepository,
    private readonly _translateService: TranslocoService,
    private readonly _helperService: HelperService,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.weekdays = this.buildWeekdays();
    this.grid = buildMonthGrid(this.monthDate.getFullYear(), this.monthDate.getMonth());

    this.categoryOptions = this._helperService.getEnumOptions(
      PreventiveTreatmentCategory,
      'animalMedical.enum.treatmentCategory',
      true
    );
    this.speciesOptions = this._helperService.getEnumOptions(AnimalCategory, 'animal.category', true);
    this.horizonOptions = [30, 60, 90].map(days => ({
      name: this._translateService.translate('dashboard.medical.horizonOption', { days }),
      value: days,
    }));

    // Bestandskennzeichen kommt nicht aus `due_treatments`, sondern aus dem Tier-Store.
    this._animalRepository.getAnimals$().pipe(takeUntil(this._destroy$)).subscribe(animals => {
      this._outOfStockIds = new Set(animals.filter(animal => !animal.inStock).map(animal => animal.id));
      this._allRows = this._allRows.map(row => ({ ...row, notInStock: this._outOfStockIds.has(row.animalId) }));
      this.recompute();
    });

    this._sheltersRepository
      .getActiveAnimalShelter$()
      .pipe(takeUntil(this._destroy$))
      .subscribe(shelter => {
        if (!shelter?.id || shelter.id === this._shelterId) return;
        this._shelterId = shelter.id;
        this.shelterName = shelter.title ?? '';
        this.loadAll();
        this.loadCalendar();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // ---------------------------------------------------------------- Laden

  /** Alles von „längst überfällig“ bis zum weitesten Zeitraum, den die Auswahl anbietet. */
  loadAll(): void {
    if (!this._shelterId) return;
    this.loadingList = true;

    const to = toIsoDateString(new Date(Date.now() + MAX_HORIZON_DAYS * 86_400_000))!;
    this._dueTreatmentRepository
      .getByShelter(this._shelterId, { from: '2000-01-01', to, onlyInStock: this.onlyInStock })
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe(rows => {
        this._allRows = groupDueTreatments(rows).map(group => this.toRow(group));
        this.loadingList = false;
        this.recompute();
        this._cdr.detectChanges();
      });
  }

  loadCalendar(): void {
    if (!this._shelterId) return;
    this.loadingCalendar = true;
    const year = this.monthDate.getFullYear();
    const month = this.monthDate.getMonth();
    this.grid = buildMonthGrid(year, month);

    this._dueTreatmentRepository
      .getByShelter(this._shelterId, { ...monthRange(year, month), onlyInStock: this.onlyInStock })
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe(rows => {
        this._monthRows = groupDueTreatments(rows).map(group => this.toRow(group));
        this.loadingCalendar = false;
        this.recompute();
        this._cdr.detectChanges();
      });
  }

  // ---------------------------------------------------------------- Filter

  showList(): void {
    this.view = 'list';
  }

  showCalendar(): void {
    this.view = 'calendar';
  }

  toggleStatus(status: DueTreatmentStatus): void {
    this.statusActive[status] = !this.statusActive[status];
    this.recompute();
  }

  onFilterChanged(): void {
    this.recompute();
  }

  /** `onlyInStock` ist ein Serverparameter, also neu laden. */
  toggleStock(): void {
    this.onlyInStock = !this.onlyInStock;
    this.loadAll();
    this.loadCalendar();
  }

  /** Aus der Überfällig-Box: in die Liste wechseln und nur Überfälliges zeigen. */
  showOverdueList(): void {
    this.view = 'list';
    this.statusActive = {
      [DueTreatmentStatus.OVERDUE]: true,
      [DueTreatmentStatus.DUE]: false,
      [DueTreatmentStatus.UPCOMING]: false,
    };
    this.recompute();
  }

  // ---------------------------------------------------------------- Kalender

  shiftMonth(months: number): void {
    this.monthDate = addMonths(this.monthDate, months);
    this.loadCalendar();
  }

  goToCurrentMonth(): void {
    const today = new Date();
    this.monthDate = new Date(today.getFullYear(), today.getMonth(), 1);
    this.selectedDay = todayIsoDate();
    this.loadCalendar();
  }

  selectDay(day: CalendarDay): void {
    this.selectedDay = day.iso;
  }

  entriesFor(iso: string): DueTreatmentRow[] {
    return this.byDay[iso] ?? [];
  }

  get selectedEntries(): DueTreatmentRow[] {
    return this.entriesFor(this.selectedDay);
  }

  get selectedDayLabel(): string {
    const date = new Date(this.selectedDay + 'T00:00:00');
    const formatted = new Intl.DateTimeFormat(this._translateService.getActiveLang(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    return this.selectedDay === todayIsoDate()
      ? `${formatted} · ${this._translateService.translate('dashboard.medical.today').toLowerCase()}`
      : formatted;
  }

  get monthLabel(): string {
    return new Intl.DateTimeFormat(this._translateService.getActiveLang(), {
      month: 'long',
      year: 'numeric',
    }).format(this.monthDate);
  }

  get asOfLabel(): string {
    return new Intl.DateTimeFormat(this._translateService.getActiveLang(), {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date());
  }

  /** Zahl für die Überfällig-Box: alles Überfällige, unabhängig von den Statusfiltern. */
  get overdueCount(): number {
    return this.applyFieldFilters(this._allRows)
      .filter(row => row.status === DueTreatmentStatus.OVERDUE).length;
  }

  get hasVisibleRows(): boolean {
    return this.sections.some(section => section.rows.length > 0);
  }

  // ---------------------------------------------------------------- Anzeige

  statusSeverity(status: DueTreatmentStatus | string | null): 'danger' | 'warn' | 'success' | 'secondary' {
    switch (status) {
      case DueTreatmentStatus.OVERDUE: return 'danger';
      case DueTreatmentStatus.DUE: return 'warn';
      case DueTreatmentStatus.UPCOMING: return 'success';
      default: return 'secondary';
    }
  }

  /** Hinweistext der KPI-Kacheln: „vor heute“, „0 – 14 Tage“, „15 – n Tage“. */
  tileHint(status: DueTreatmentStatus): string {
    switch (status) {
      case DueTreatmentStatus.OVERDUE:
        return this._translateService.translate('dashboard.medical.tiles.hintOverdue');
      case DueTreatmentStatus.DUE:
        return this._translateService.translate('dashboard.medical.tiles.hintDue');
      default:
        return this._translateService.translate('dashboard.medical.tiles.hintUpcoming', { days: this.horizonDays });
    }
  }

  /** „Eintragen“ und Zeilenklick: zum Tier, direkt in die Sektion Prophylaxe. */
  openAnimal(row: DueTreatmentRow): void {
    this._router.navigate(['/tiere', row.animalId], {
      queryParams: { tabNumber: 6, medicalSection: 'prophylaxis' }
    });
  }

  // ---------------------------------------------------------------- Intern

  /**
   * Kategorie und Tierart filtert das Backend nicht (`due_treatments` kennt nur `shelter`,
   * `animal`, `from`, `to`, `status`, `onlyInStock`, `limit`) — also clientseitig.
   */
  private applyFieldFilters(rows: DueTreatmentRow[]): DueTreatmentRow[] {
    return rows.filter(row =>
      (!this.category || row.category === this.category) &&
      (!this.species || row.animalCategory === this.species)
    );
  }

  private recompute(): void {
    const base = this.applyFieldFilters(this._allRows);

    this.tiles = STATUS_ORDER.map(status => ({
      key: status,
      active: this.statusActive[status],
      count: base.filter(row =>
        row.status === status &&
        (status !== DueTreatmentStatus.UPCOMING || row.daysUntilDue <= this.horizonDays)
      ).length,
    }));

    const listRows = base.filter(row =>
      this.statusActive[row.status] && row.daysUntilDue <= this.horizonDays
    );
    this.sections = STATUS_ORDER
      .filter(status => this.statusActive[status])
      .map(status => ({ key: status, rows: listRows.filter(row => row.status === status) }))
      .filter(section => section.rows.length > 0);

    const monthVisible = this.applyFieldFilters(this._monthRows)
      .filter(row => this.statusActive[row.status]);
    this.byDay = groupDueTreatmentsByDay(monthVisible);
  }

  private toRow(group: DueTreatmentGroup): DueTreatmentRow {
    const typeLabels = group.types.map(type =>
      this._translateService.translate(getPreventiveTreatmentTypeLabelKey(type))
    );
    const categoryLabel = this._translateService.translate(
      getPreventiveTreatmentCategoryLabelKey(group.category)
    );
    const name = group.animalName?.trim() || '';

    return {
      ...group,
      typeLabels,
      categoryLabel,
      label: typeLabels.length ? typeLabels.join(', ') : categoryLabel,
      speciesLabel: group.animalCategory
        ? this._translateService.translate(`animal.category.${group.animalCategory}`)
        : '',
      animalLabel: [name, group.bookNumber].filter(Boolean).join(' · ') || `#${group.animalId}`,
      initial: (name || '?').charAt(0).toUpperCase(),
      avatarColor: SPECIES_COLORS[group.animalCategory ?? ''] ?? DEFAULT_AVATAR_COLOR,
      daysLabel: this.daysLabel(group.daysUntilDue),
      vetName: this.veterinarianName(group),
      notInStock: this._outOfStockIds.has(group.animalId),
    };
  }

  private daysLabel(days: number): string {
    if (days === 0) return this._translateService.translate('dashboard.medical.dueToday');
    const key = days < 0 ? 'animalMedical.prophylaxis.daysOverdue' : 'animalMedical.prophylaxis.daysLeft';
    return this._translateService.translate(key, { days: Math.abs(days) });
  }

  /** Praxis über `companyName`, sonst Vor- und Nachname. */
  private veterinarianName(group: DueTreatmentGroup): string {
    const vet = group.veterinarian;
    if (!vet) return '–';
    const company = vet.companyName?.trim();
    if (company) return company;
    return [vet.firstName, vet.lastName].map(part => part?.trim()).filter(Boolean).join(' ') || '–';
  }

  /** Montag bis Sonntag in der aktiven Sprache, ohne Angular-Locale-Registrierung. */
  private buildWeekdays(): string[] {
    const format = new Intl.DateTimeFormat(this._translateService.getActiveLang(), { weekday: 'short' });
    // 05.01.2026 ist ein Montag.
    return Array.from({ length: 7 }, (_, index) => format.format(new Date(2026, 0, 5 + index)));
  }
}
